import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './ProjectFive.css';

const STEPS = [
  {
    num: '01',
    label: 'WRITE THE 5-YEAR VISION',
    desc: 'The big one. The life you are actually going to build over the next five years. Write it as if it already exists. dmyor turns it into a real plan, not a wish on a page.',
  },
  {
    num: '02',
    label: 'BREAK IT INTO A PLAN',
    desc: 'Your vision becomes milestones. Milestones become actions small enough to start today. With Dmyor Assist you go from one sentence to a full, structured path you can actually walk.',
  },
  {
    num: '03',
    label: 'EXECUTE IT DAILY',
    desc: 'Execute mode pulls every actionable piece into one focused page. Do today\'s work, build the habits underneath it, check things off, and move the plan forward for real.',
  },
  {
    num: '04',
    label: 'DOCUMENT THE JOURNEY',
    desc: 'Post updates on any part of the plan as you go. Status moves from standby to active to done. Five years of honest, timestamped progress builds itself into a record only you could write.',
  },
  {
    num: '05',
    label: 'RELEASE WHEN IT IS REAL',
    desc: 'When the vision becomes reality, you release it. It stops being anonymous and goes public in the intent archive. Proof, on the record, that you set out to build something and did.',
  },
];

const FEATURES = [
  {
    num: '01',
    label: 'LIVE 5-YEAR COUNTDOWN',
    desc: 'Your workspace shows exactly how many days remain in your five-year window, with a progress bar on the vision itself. Nothing lets you pretend the time isn\'t moving.',
    tag: 'ALWAYS ON',
  },
  {
    num: '02',
    label: 'THE PLANNING TREE',
    desc: 'Not a document you read once. A living tree of milestones and actions, each a trackable node you move standby to active to done. The whole plan, visible at a glance.',
    tag: 'THE CORE',
  },
  {
    num: '03',
    label: 'EXECUTE MODE',
    desc: 'The plan, flattened into one daily page. Pin what matters, check it off, log what you did. Turns a five-year vision into something you actually run on a Tuesday.',
    tag: 'DAILY DRIVER',
  },
  {
    num: '04',
    label: 'HABITS & JOURNAL',
    desc: 'Track the daily reps that compound over years, with streaks tied to your plan. And write the story as it happens, a record of how you made it real.',
    tag: 'THE LONG GAME',
  },
  {
    num: '05',
    label: 'DMYOR ASSIST',
    desc: 'AI plugged into your entire plan. Ask it to break things down, draft documents, set habits, or add todos. It suggests, you approve. The system runs the plan, the AI supports it.',
    tag: 'AI-POWERED',
  },
  {
    num: '06',
    label: 'THE INTENT ARCHIVE',
    desc: 'Private by default, yours until you decide otherwise. Release your vision when it is real and it joins a public record of what people set out to do and actually pulled off.',
    tag: 'YOUR CHOICE',
  },
];

const ProjectFive = () => {
  const canvasRef = useRef(null);
  const [glitch, setGlitch] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const dots = Array.from({ length: 120 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.2 + 0.3,
      speed: Math.random() * 0.3 + 0.05,
      color: ['#ffe600', '#ff003c', '#ff6600', '#ff2255'][Math.floor(Math.random() * 4)],
      opacity: Math.random() * 0.5 + 0.1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      dots.forEach((d) => {
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = d.color;
        ctx.globalAlpha = d.opacity;
        ctx.fill();
        d.y -= d.speed;
        if (d.y < -4) { d.y = canvas.height + 4; d.x = Math.random() * canvas.width; }
      });
      ctx.globalAlpha = 1;
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(animId); };
  }, []);

  useEffect(() => {
    const scheduleGlitch = () => {
      const delay = 600 + Math.random() * 1200;
      return setTimeout(() => {
        setGlitch(true);
        const duration = 80 + Math.random() * 100;
        setTimeout(() => {
          setGlitch(false);
          if (Math.random() < 0.3) {
            setTimeout(() => {
              setGlitch(true);
              setTimeout(() => setGlitch(false), 60);
            }, 80);
          }
        }, duration);
        t = scheduleGlitch();
      }, delay);
    };
    let t = scheduleGlitch();
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="p5-page">
      <Helmet>
        <title>PROJECT-5 | 5 Years. One Life. No Excuses.</title>
        <link rel="canonical" href="https://www.dmyor.com/project-5" />
        <meta name="description" content="PROJECT-5 is a 5-year commitment to building the life you want. Write the vision, break it into a real plan with Dmyor Assist, then execute it day by day for five years." />
      </Helmet>

      <canvas className="p5-canvas" ref={canvasRef} />
      <div className="p5-scanlines" aria-hidden="true" />

      {/* ── HERO ── */}
      <section className="p5-hero">
        <div className="p5-hero-inner">
          <div className="p5-system-tag">DMYOR // PROJECT-5 // 2026</div>

          <h1 className={`p5-title ${glitch ? 'glitch' : ''}`} data-text="PROJECT-5">
            PROJECT-5
          </h1>

          <div className="p5-tagline-row">
            <span className="p5-tag-item p5-green">5 YEARS</span>
            <span className="p5-tag-sep">·</span>
            <span className="p5-tag-item p5-red">ONE LIFE</span>
            <span className="p5-tag-sep">·</span>
            <span className="p5-tag-item p5-cyan">NO EXCUSES</span>
          </div>

          <p className="p5-hero-sub">
            Five years from now, your life will be somewhere. The question is
            whether you decided where, or just let it happen. PROJECT-5 is a
            commitment to design your future and actually build it. Write the
            vision, let dmyor break it into a real plan, then execute it day by
            day. Stay with it for five years and you walk into the life you built
            here.
          </p>

          <div className="p5-hero-cta">
            <Link to="/signup" className="p5-btn-primary">
              <span className="p5-btn-bracket">[</span>
              START PROJECT-5
              <span className="p5-btn-bracket">]</span>
            </Link>
            <Link to="/about" className="p5-btn-ghost">WHAT IS DMYOR</Link>
          </div>

          <div className="p5-scroll-hint">SCROLL DOWN ↓</div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="p5-objectives">
        <div className="p5-section-tag">HOW IT WORKS</div>
        <h2 className="p5-section-title">Five steps. Five years.</h2>
        <div className="p5-obj-list">
          {STEPS.map((s) => (
            <div className="p5-obj-row" key={s.num}>
              <div className="p5-obj-num">{s.num}</div>
              <div className="p5-obj-body">
                <div className="p5-obj-label">{s.label}</div>
                <p className="p5-obj-desc">{s.desc}</p>
              </div>
              <div className="p5-obj-bar">
                <div className="p5-obj-fill" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHY WRITE ── */}
      <section className="p5-brief">
        <div className="p5-brief-inner">
          <div className="p5-section-tag">WHY IT MATTERS</div>
          <h2 className="p5-section-title">
            You have had this thought
            <span className="p5-green">before</span>
          </h2>
          <div className="p5-brief-body">
            <p>
              A clear picture of the life you want. Who you would be, what you
              would have, what it would feel like. And then life moved on, and
              that picture stayed in your head. Quiet. Unwritten. And slowly, it
              faded. Again.
            </p>
            <p>
              Writing something down changes what it is. It leaves your
              subconscious and enters the real world. It stops being a passing
              thought and becomes a claim. Your future self will read what you
              write here. They will see what you wanted, what scared you, how
              hard you were trying. They will know you showed up.
            </p>
            <p className="p5-brief-highlight">
              Not just for you. Anyone who comes after and wants to understand
              what it was like to be alive right now, wanting something and
              building toward it, will find it here. Don't let five years pass
              without a record.
            </p>
          </div>
        </div>
      </section>

      {/* ── ALL OUT ── */}
      <section className="p5-allout">
        <div className="p5-allout-inner">
          <div className="p5-section-tag">THE CASE</div>
          <h2 className="p5-allout-title">
            Because why not give life<br />
            a <span className="p5-green">serious, all-out effort</span><br />
            to get what you want.
          </h2>
          <p className="p5-allout-body">
            You are going to spend the next five years alive regardless. Working,
            sleeping, scrolling, existing. The time is going whether you use it or
            not. So the only real question is whether you spend it deliberately.
            Committing to what matters to you, moving toward it, and building a
            record of someone who actually tried. The worst case is you tried and
            fell short. The best case is you get exactly the life you designed.
            Both are better than never starting.
          </p>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="p5-features">
        <div className="p5-features-inner">
          <div className="p5-section-tag">WHAT YOU GET</div>
          <h2 className="p5-section-title">Built to keep you on track.</h2>
          <div className="p5-feat-list">
            {FEATURES.map((f) => (
              <div className="p5-feat-row" key={f.num}>
                <div className="p5-feat-num">{f.num}</div>
                <div className="p5-feat-body">
                  <div className="p5-feat-header">
                    <span className="p5-feat-label">{f.label}</span>
                    <span className="p5-feat-tag">{f.tag}</span>
                  </div>
                  <p className="p5-feat-desc">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="p5-cta">
        <div className="p5-cta-inner">
          <div className="p5-system-tag">YOUR CALL</div>
          <h2 className="p5-cta-title">
            Are you in<span className="p5-red">?</span>
          </h2>
          <p className="p5-cta-sub">
            Five years from today, this either happened or it didn't.
            The only variable is what you decide right now.
          </p>
          <Link to="/signup" className="p5-btn-primary p5-btn-large">
            <span className="p5-btn-bracket">[</span>
            START PROJECT-5
            <span className="p5-btn-bracket">]</span>
          </Link>
          <div className="p5-cta-note">
            Already on dmyor? <Link to="/login" className="p5-link">Log in →</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectFive;
